import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { writeBundleEditSnapshot } from '@theme/bundle-builder-edit-session';
import { getInvalidCartLineKeys } from '@theme/cart-validation-utils';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Cart line item serialized from `snippets/cart-json.liquid`.
 *
 * @typedef {Object} CartItem
 * @property {number} id - The variant id of the cart item.
 * @property {string} key - The unique key of the cart line.
 * @property {string} handle - The product handle.
 * @property {string[]} tags - Product tags for validation.
 * @property {string} title - The title of the cart item.
 * @property {string | null} image - The featured image URL of the cart item.
 * @property {number} price - The price of the cart item.
 * @property {number} original_price - The original price of the cart item.
 * @property {number} discounted_price - The discounted price of the cart item.
 * @property {number} quantity - The quantity of the cart item.
 * @property {Record<string, string>} properties - Line item properties.
 * @property {number} variant_id - The variant id of the cart item.
 * @property {number} line_price - The line price of the cart item.
 * @property {number} original_line_price - The original line price of the cart item.
 * @property {number} total_discount - The total discount of the cart item.
 * @property {Array<{ amount: number, discount_application?: { title?: string } }>} discounts - Line-level discount allocations.
 * @property {string | null} sku - The sku of the cart item.
 * @property {number} grams - The grams of the cart item.
 * @property {string} vendor - The vendor of the cart item.
 * @property {boolean} taxable - Whether the cart item is taxable.
 * @property {{ id: number } | null} gwp - Associated gwp variant.
 */

/**
 * Cart payload from `data-cart` on `<cart-items-component />`.
 *
 * @typedef {Object} Cart
 * @property {string | null} note - The cart note.
 * @property {Record<string, string>} attributes - Cart attributes.
 * @property {CartItem[]} items - The items in the cart.
 * @property {number} original_total_price - The original total price of the cart.
 * @property {number} total_price - The total price of the cart.
 * @property {number} total_discount - The total discount of the cart.
 * @property {number} total_weight - The total weight of the cart.
 * @property {number} item_count - The item count of the cart.
 */

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} [cartValidationNotice] - Cart-level validation notice.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {{ title: string, image: string | null }[] | undefined} */
  #removedLineTitles = undefined;

  /** @type {Element | null} */
  #cartDrawer = null;

  /** @type {boolean} */
  static #isRemovingInvalidLines = false;

  /** @type {Promise<void>} */
  static #cartMaintenancePromise = Promise.resolve();

  /**
   * Prefer the main cart instance for GWP/validation maintenance; fall back to drawer.
   * @returns {CartItemsComponent | null}
   */
  static #getCartMaintenanceInstance() {
    const mainCart = document.querySelector('cart-items-component:not([data-drawer])');
    if (mainCart instanceof CartItemsComponent) return mainCart;

    const drawerCart = document.querySelector('cart-items-component[data-drawer]');
    if (drawerCart instanceof CartItemsComponent) return drawerCart;

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    if (this.hasAttribute('data-drawer')) {
      this.#cartDrawer = this.closest('cart-drawer-component');
      this.#cartDrawer?.addEventListener(DialogCloseEvent.eventName, this.#handleDrawerClose);
    }

    setTimeout(() => {
      this.#onCartUpdate();
    }, 10);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#cartDrawer?.removeEventListener(DialogCloseEvent.eventName, this.#handleDrawerClose);
    this.#cartDrawer = null;
  }

  #handleDrawerClose = () => {
    this.#dismissCartValidationNotice();
  };

  /**
   * @returns {Cart} The cart.
   */
  get cart() {
    /** @type {Cart} */
    const defaultCart = {
      note: null,
      attributes: {},
      items: [],
      original_total_price: 0,
      total_price: 0,
      total_discount: 0,
      total_weight: 0,
      item_count: 0,
    };

    try {
      const parsed = JSON.parse(this.dataset.cart ?? '{}');
      if (parsed && typeof parsed === 'object') {
        return /** @type {Cart} */ ({
          ...defaultCart,
          ...parsed,
          items: Array.isArray(parsed.items) ? parsed.items : [],
        });
      }
    } catch {
      // Malformed or missing data-cart attribute.
    }

    return defaultCart;
  }

  /**
   * Handles the cart update event.
   * @returns {Promise<void>}
   */
  #onCartUpdate = async () => {
    if (CartItemsComponent.#getCartMaintenanceInstance() !== this) return;

    const previousMaintenance = CartItemsComponent.#cartMaintenancePromise;
    /** @type {() => void} */
    let releaseMaintenanceLock = () => {};
    CartItemsComponent.#cartMaintenancePromise = new Promise((resolve) => {
      releaseMaintenanceLock = resolve;
    });

    await previousMaintenance;

    try {
      const hasModifiedGWP = await this.#addOrRemoveGWP();
      if (hasModifiedGWP) return;
      await this.#validateCart();
    } finally {
      releaseMaintenanceLock();
    }
  };

  /**
   * Qualifying parent lines for GWP: have a cart_gwp metafield (or parent-shell
   * metafield resolved in cart-json for Recharge bundles), have not had the gift
   * dismissed, and are not the gift line itself.
   *
   * Recharge `_rc_bundle` groups contribute at most one parent. If any line in the
   * group is dismissed, the whole group is ineligible so the gift is not re-added
   * from a sibling component SKU.
   * @returns {CartItem[]}
   */
  #getQualifyingGwpParents() {
    /** @type {CartItem[]} */
    const parents = [];
    /** @type {Set<string>} */
    const seenRcBundles = new Set();

    for (const item of this.cart.items) {
      if (!item.gwp || item.properties?._is_gwp || item.properties?._gwp_dismissed) continue;

      const rcBundle = item.properties?._rc_bundle;
      if (rcBundle) {
        if (seenRcBundles.has(rcBundle)) continue;
        if (this.#isRcBundleGroupGwpDismissed(rcBundle)) continue;
        seenRcBundles.add(rcBundle);
      }

      parents.push(item);
    }

    return parents;
  }

  /**
   * @param {string} rcBundle
   * @returns {boolean}
   */
  #isRcBundleGroupGwpDismissed(rcBundle) {
    return this.cart.items.some(
      (item) => item.properties?._rc_bundle === rcBundle && item.properties?._gwp_dismissed
    );
  }

  /**
   * Resolves the parent line key for a GWP gift.
   * - Prefer `_gwp_parent_key` when that line still exists (even if already dismissed).
   * - If the stored key is gone (Shopify key rotation), prefer an already-dismissed
   *   parent that awards this gift variant — never fall through to an unrelated
   *   qualifying parent (that would wrongly block another product's gift).
   * - Fall back to the first qualifying parent only for legacy gifts with no stored key.
   *
   * @param {CartItem} gwpItem - The gift line being dismissed.
   * @returns {string | undefined}
   */
  #resolveGwpParentKey(gwpItem) {
    const storedKey = gwpItem.properties?._gwp_parent_key;
    if (storedKey) {
      const storedParent = this.cart.items.find((item) => item.key === storedKey);
      if (storedParent && !storedParent.properties?._is_gwp && storedParent.gwp) {
        return storedKey;
      }

      const giftVariantId = gwpItem.variant_id ?? gwpItem.id;
      const dismissedForThisGift = this.cart.items.find(
        (item) =>
          !item.properties?._is_gwp &&
          item.gwp?.id === giftVariantId &&
          item.properties?._gwp_dismissed
      );
      return dismissedForThisGift?.key;
    }

    return this.#getQualifyingGwpParents()[0]?.key;
  }

  /**
   * Adds or removes a GWP from the cart.
   * Gift lines are always one-time (no selling_plan / no `_rc_bundle`) so they do
   * not become recurring subscription items with the qualifying parent.
   * @returns {Promise<boolean>} Whether a GWP was added or removed.
   */
  #addOrRemoveGWP = async () => {
    const qualifyingParents = this.#getQualifyingGwpParents();
    const gwpToBePotentiallyAdded = qualifyingParents.map((item) => item.gwp?.id);
    const preexistingGWPOnCart = this.cart.items.filter(
      (item) => item.properties?._auto_added && item.properties?._is_gwp
    );
    /** @type {Record<string, number>} */
    const cartUpdates = {};

    /** 1. Remove any existing GWP that should no longer be in the cart*/
    for (const preexistingGWP of preexistingGWPOnCart) {
      if (!gwpToBePotentiallyAdded.includes(preexistingGWP.id)) {
        cartUpdates[String(preexistingGWP.key)] = 0;
      }
    }
    if (Object.keys(cartUpdates).length > 0) {
      return await this.#applyCartUpdates(cartUpdates, 'cart-items-gwp-update');
    }

    /** 2. Keep ONLY 1 GWP in the cart */
    if (preexistingGWPOnCart.length) {
      const [firstGWP, ...excedentGWP] = preexistingGWPOnCart;
      for (const gwp of excedentGWP) {
        cartUpdates[String(gwp.key)] = 0;
      }
      if (firstGWP?.quantity && firstGWP.quantity > 1) {
        cartUpdates[String(firstGWP.key)] = 1;
      }
      if (Object.keys(cartUpdates).length > 0) {
        return await this.#applyCartUpdates(cartUpdates, 'cart-items-gwp-update');
      }
    }

    /** 3. Add 1 GWP to the cart (one-time line — no selling_plan) */
    if (gwpToBePotentiallyAdded.length > 0 && !preexistingGWPOnCart.length) {
      const parent = qualifyingParents[0];
      const gwpVariantId = parent?.gwp?.id;
      if (!gwpVariantId || !parent?.key) return false;

      return await this.#addToCart(
        [
          {
            id: gwpVariantId,
            quantity: 1,
            properties: {
              _auto_added: true,
              _is_gwp: true,
              _gwp_parent_key: parent.key,
            },
          },
        ],
        'cart-items-gwp-add'
      );
    }

    /** 4. No GWP to add or remove */
    return false;
  };

  /**
   * Validates the cart items and removes invalid lines.
   * Shows a cart validation notice if there are invalid lines.
   * @returns {Promise<void>}
   */
  #validateCart = async () => {
    if (CartItemsComponent.#isRemovingInvalidLines) return;

    const cartItems = this.cart.items ?? [];
    const invalidLineKeys = getInvalidCartLineKeys(cartItems);

    if (!invalidLineKeys.length) {
      this.#dismissCartValidationNotice();
      return;
    }

    const invalidLines = cartItems
      .filter((item) => invalidLineKeys.includes(item.key))
      .map((item) => ({ title: item.title, image: item.image ?? null }));

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of invalidLineKeys) {
      updates[key] = 0;
    }

    CartItemsComponent.#isRemovingInvalidLines = true;

    const success = await this.#applyCartUpdates(updates, 'cart-items-validation');
    if (!success) {
      CartItemsComponent.#isRemovingInvalidLines = false;
      return;
    }

    this.#removedLineTitles = invalidLines;
    requestAnimationFrame(() => {
      this.#showCartValidationNotice();
    });
  };

  /**
   * Shows the cart-level notice after invalid lines were removed.
   */
  #showCartValidationNotice() {
    if (!this.#removedLineTitles?.length) return;

    const noticeRef = this.refs.cartValidationNotice;
    const noticeListRef = this.refs.cartValidationNoticeList;

    if (!(noticeRef instanceof HTMLElement) || !(noticeListRef instanceof HTMLElement)) {
      this.#removedLineTitles = undefined;
      CartItemsComponent.#isRemovingInvalidLines = false;
      return;
    }

    noticeListRef.replaceChildren();

    for (const { title, image } of this.#removedLineTitles) {
      const listItem = document.createElement('li');
      listItem.className = 'cart-validation-notice__item';

      if (image) {
        const thumbnail = document.createElement('img');
        thumbnail.className = 'cart-validation-notice__thumbnail';
        thumbnail.src = image;
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        thumbnail.width = 48;
        thumbnail.height = 48;
        listItem.append(thumbnail);
      }

      const titleElement = document.createElement('span');
      titleElement.className = 'cart-validation-notice__title';
      titleElement.textContent = title;
      listItem.append(titleElement);
      noticeListRef.append(listItem);
    }

    noticeRef.hidden = false;
    noticeRef.classList.remove('hidden');
    this.#removedLineTitles = undefined;
    CartItemsComponent.#isRemovingInvalidLines = false;
  }

  /**
   * Dismisses the cart validation notice.
   */
  #dismissCartValidationNotice() {
    const noticeRef = this.refs.cartValidationNotice;
    if (!(noticeRef instanceof HTMLElement)) return;
    noticeRef.hidden = true;
    noticeRef.classList.add('hidden');
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    this.#animateRowRemoval(line);
  }

  /**
   * Dismisses a GWP gift: flags the linked parent with `_gwp_dismissed`, then
   * removes the gift line so auto-add will not re-add it.
   * @param {number} line - The 1-based cart line index of the GWP item.
   */
  async onGwpRemove(line) {
    const gwpItem = this.cart.items[line - 1];
    if (!gwpItem?.properties?._is_gwp) return;

    const parentKey = this.#resolveGwpParentKey(gwpItem);
    let gwpKey = gwpItem.key;

    // Flag parent silently (no morph) before removing the gift, so the UI does
    // not flash the gift back while the parent property update is in flight.
    // Skip when the linked parent is already dismissed (retry after a failed gift remove).
    if (parentKey) {
      const parent = this.cart.items.find((item) => item.key === parentKey);
      const alreadyDismissed = Boolean(parent?.properties?._gwp_dismissed);

      if (!alreadyDismissed) {
        const flagged = await this.#updateLineItemProperties(
          parentKey,
          { _gwp_dismissed: 'true' },
          { silent: true }
        );
        if (!flagged) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }
      }
    }

    gwpKey =
      this.cart.items.find((item) => item.key === gwpKey && item.properties?._is_gwp)?.key ??
      this.cart.items.find((item) => item.properties?._is_gwp && item.properties?._auto_added)?.key ??
      gwpKey;

    const removed = await this.#applyCartUpdates({ [gwpKey]: 0 }, 'cart-items-gwp-dismiss');
    if (!removed) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * Optimistically animates removal of a cart row (and nested children).
   * @param {number} line - The 1-based cart line index (fallback when key is omitted).
   * @param {string} [key] - Cart line key; preferred when bundle rows collapse the table.
   */
  #animateRowRemoval(line, key) {
    const cartItemRows = this.refs.cartItemRows ?? [];
    const cartItemRowToRemove =
      (key && cartItemRows.find((row) => row.dataset.key === key)) || cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    for (const row of rowsToRemove) {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        remove();
        continue;
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    }
  }

  /**
   * Starts editing a bundle from the cart drawer.
   * Reads bundle data from `data-bundle-edit` on the clicked link, writes a snapshot
   * to sessionStorage, and navigates to the bundle builder.
   *
   * @param {string|Event} [editData]
   * @param {Event} [event]
   */
  onBundleEdit(editData, event) {
    if (editData instanceof Event) {
      event = editData;
      editData = '';
    }

    event?.preventDefault?.();

    if (!editData && event?.target instanceof Element) {
      const trigger = event.target.closest('[data-bundle-edit]');
      editData = trigger?.getAttribute('data-bundle-edit') ?? '';
    }

    if (typeof editData !== 'string' || !editData) return;

    /** @type {{ bundleKey?: string, purchaseType?: string, sellingPlanId?: string | null, lines?: Array<{ productId: number, variantId: number, quantity: number, key: string }>, bundleBuilderUrl?: string }} */
    let parsed;
    try {
      parsed = JSON.parse(editData);
    } catch {
      return;
    }

    const bundleKey = parsed.bundleKey;
    const lines = parsed.lines;
    if (!bundleKey || !Array.isArray(lines) || lines.length === 0) return;

    /** @type {Map<number, { productId: number, quantity: number }>} */
    const itemsByProduct = new Map();
    /** @type {string[]} */
    const cartLineKeys = [];

    for (const line of lines) {
      if (!line?.key) continue;
      cartLineKeys.push(String(line.key));
      const productId = Number(line.productId);
      const quantity = Number(line.quantity);
      if (!productId || !quantity) continue;

      const existing = itemsByProduct.get(productId);
      if (existing) {
        existing.quantity += quantity;
      } else {
        itemsByProduct.set(productId, { productId, quantity });
      }
    }

    if (cartLineKeys.length === 0) return;

    const purchaseType = /** @type {'one-time' | 'subscribe'} */ (
      parsed.purchaseType === 'one-time' ? 'one-time' : 'subscribe'
    );
    const sellingPlanId =
      purchaseType === 'subscribe' && parsed.sellingPlanId != null
        ? String(parsed.sellingPlanId)
        : null;

    const snapshot = {
      bundleKey: String(bundleKey),
      purchaseType,
      sellingPlanId,
      items: Array.from(itemsByProduct.values()),
      cartLineKeys,
    };

    try {
      writeBundleEditSnapshot(snapshot);
    } catch {
      return;
    }

    const bundleBuilderUrl = parsed.bundleBuilderUrl || '/pages/bundle-builder';
    const separator = bundleBuilderUrl.includes('?') ? '&' : '?';
    window.location.href = `${bundleBuilderUrl}${separator}edit=${encodeURIComponent(String(bundleKey))}`;
  }

  /**
   * Removes all line items that share a bundle group.
   * Reads keys from `data-bundle-keys` on the clicked remove button, or from the first argument.
   *
   * @param {string|string[]|Event} [keys]
   * @param {Event} [event]
   */
  async onBundleRemove(keys, event) {
    if (keys instanceof Event) {
      event = keys;
      keys = '';
    }

    event?.preventDefault?.();

    if (!keys && event?.target instanceof Element) {
      const trigger = event.target.closest('[data-bundle-keys]');
      keys = trigger?.getAttribute('data-bundle-keys') ?? '';
    }

    /** @type {string[]} */
    let parsedKeys = [];
    if (Array.isArray(keys)) {
      parsedKeys = keys.map(String).filter(Boolean);
    } else if (typeof keys === 'string') {
      try {
        const maybe = JSON.parse(keys);
        if (Array.isArray(maybe)) parsedKeys = maybe.map(String).filter(Boolean);
      } catch {
        parsedKeys = [keys].filter(Boolean);
      }
    }

    if (parsedKeys.length === 0) return;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of parsedKeys) {
      updates[key] = 0;
    }

    await this.#applyCartUpdates(updates, 'cart-items-bundle-remove');
  }

  /**
   * Gets the cart body for the cart update request.
   * @param {Object} payload - The payload.
   * @returns {string} The cart body.
   */
  #getCartBodyWithSections(payload) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return JSON.stringify({
      ...payload,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
  }

  /**
   * Disables the cart items and shimmers the cart total.
   * @param {string} source - The source of the cart update.
   */
  #beforeCartUpdate(source) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${source}:user-action`);
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();
    return cartPerformaceUpdateMarker;
  }

  /**
   * Resets the shimmer and dispatches the cart update event.
   * @param {Response} response - The cart update/add response.
   * @param {string} source - The source of the cart update.
   * @returns {Promise<boolean>} Whether the cart update succeeded.
   */
  async #afterCartUpdate(response, source) {
    const parsedResponseText = JSON.parse(await response.text());
    resetShimmer(this);
    if (!response.ok || parsedResponseText.status || parsedResponseText.errors) {
      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          didError: true,
          source,
          sections: parsedResponseText.sections,
        })
      );
      return false;
    }
    const newSectionHTML = new DOMParser().parseFromString(
      parsedResponseText.sections[this.sectionId],
      'text/html'
    );
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source,
        sections: parsedResponseText.sections,
      })
    );

    await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
    this.#syncDataCartFromSections(parsedResponseText.sections);
    this.#updateCartQuantitySelectorButtonStates();
    return true;
  }


  /**
   * Updates cart line quantities in a single request and re-renders cart sections.
   *
   * @param {Record<string, number>} updates - Line item keys mapped to quantities.
   * @param {string} source - Event source identifier for cart:update listeners.
   * @returns {Promise<boolean>} Whether the cart update succeeded.
   */
  async #applyCartUpdates(updates, source) {
    if (Object.keys(updates).length === 0) return false;
    const cartPerformaceUpdateMarker = this.#beforeCartUpdate(source);

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body: this.#getCartBodyWithSections({ updates }) }));
      return await this.#afterCartUpdate(response, source);
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      return false;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Merges property updates onto a cart line. Shopify replaces the full
   * properties object on change, so existing properties are preserved.
   *
   * @param {string} key - Cart line item key.
   * @param {Record<string, string>} propertyUpdates - Properties to set or override.
   * @param {{ silent?: boolean }} [options] - When `silent`, skip section morph and
   *   cart:update events (used when a follow-up request will re-render).
   * @returns {Promise<boolean>} Whether the update succeeded.
   */
  async #updateLineItemProperties(key, propertyUpdates, { silent = false } = {}) {
    const item = this.cart.items.find((cartItem) => cartItem.key === key);
    if (!item) return false;

    const mergedProperties = {
      ...item.properties,
      ...propertyUpdates,
    };
    const cartPerformaceUpdateMarker = this.#beforeCartUpdate('cart-items-gwp-properties');

    try {
      const payload = {
        id: key,
        quantity: item.quantity,
        properties: mergedProperties,
      };
      const body = silent ? JSON.stringify(payload) : this.#getCartBodyWithSections(payload);
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));

      if (silent) {
        const parsedResponseText = JSON.parse(await response.text());
        resetShimmer(this);
        if (!response.ok || parsedResponseText.status || parsedResponseText.errors) {
          return false;
        }
        this.#applyCartChangeToDataCart(key, parsedResponseText);
        return true;
      }

      return await this.#afterCartUpdate(response, 'cart-items-gwp-properties');
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      return false;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Finds the cart line in a /cart/change response that corresponds to `previousKey`.
   * When Shopify rotates the key after a property update, match only among keys that
   * were not present before the request — never the first line with the same variant_id
   * (duplicate variants would corrupt the wrong line).
   *
   * @param {string} previousKey
   * @param {{ items?: Array<{ key: string, variant_id: number, quantity?: number, properties?: Record<string, string> }> }} cartResponse
   * @returns {{ key: string, variant_id: number, quantity?: number, properties?: Record<string, string> } | undefined}
   */
  #findChangedCartLine(previousKey, cartResponse) {
    if (!cartResponse?.items) return undefined;

    const bySameKey = cartResponse.items.find((item) => item.key === previousKey);
    if (bySameKey) return bySameKey;

    const previousItem = this.cart.items.find((item) => item.key === previousKey);
    if (!previousItem) return undefined;

    const previousKeys = new Set(this.cart.items.map((item) => item.key));
    const rotatedCandidates = cartResponse.items.filter(
      (item) => !previousKeys.has(item.key) && item.variant_id === previousItem.variant_id
    );

    if (rotatedCandidates.length === 1) return rotatedCandidates[0];

    // Ambiguous duplicate-variant rotation: prefer the line that received _gwp_dismissed.
    if (rotatedCandidates.length > 1) {
      return rotatedCandidates.find((item) => item.properties?._gwp_dismissed);
    }

    return undefined;
  }

  /**
   * Merges a /cart/change response into live `data-cart` without morphing.
   * Preserves theme-only fields (gwp, tags, handle) while adopting rotated
   * line keys and updated properties from Shopify.
   *
   * @param {string} previousKey - Line key sent in the change request.
   * @param {{ items?: Array<{ key: string, variant_id: number, quantity?: number, properties?: Record<string, string> }>, item_count?: number, total_price?: number, items_subtotal_price?: number, total_discount?: number }} cartResponse
   */
  #applyCartChangeToDataCart(previousKey, cartResponse) {
    const responseItem = this.#findChangedCartLine(previousKey, cartResponse);
    if (!responseItem) return;

    const nextKey = responseItem.key;
    const nextProperties = responseItem.properties ?? {};

    for (const el of document.querySelectorAll('cart-items-component')) {
      if (!(el instanceof HTMLElement) || !el.dataset.cart) continue;

      try {
        const parsed = JSON.parse(el.dataset.cart);
        if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.items)) continue;

        parsed.items = parsed.items.map((cartItem) => {
          if (cartItem.key === previousKey) {
            return {
              ...cartItem,
              key: nextKey,
              quantity: responseItem.quantity ?? cartItem.quantity,
              properties: { ...cartItem.properties, ...nextProperties },
            };
          }

          if (cartItem.properties?._gwp_parent_key === previousKey) {
            return {
              ...cartItem,
              properties: { ...cartItem.properties, _gwp_parent_key: nextKey },
            };
          }

          return cartItem;
        });

        if (typeof cartResponse.item_count === 'number') parsed.item_count = cartResponse.item_count;
        if (typeof cartResponse.total_price === 'number') parsed.total_price = cartResponse.total_price;
        if (typeof cartResponse.items_subtotal_price === 'number') {
          parsed.items_subtotal_price = cartResponse.items_subtotal_price;
        }
        if (typeof cartResponse.total_discount === 'number') parsed.total_discount = cartResponse.total_discount;

        el.dataset.cart = JSON.stringify(parsed);
      } catch {
        // Malformed data-cart; skip this host.
      }
    }
  }

  /**
   * Adds items to the cart.
   * @param {Array<{ id: number | string, quantity: number, properties?: Record<string, string | number | boolean | null | undefined> }>} items - The items to add.
   * @param {string} source - The source of the cart update.
   * @returns {Promise<boolean>} Whether the cart update succeeded.
   */
  async #addToCart(items, source) {
    if (!items || !Array.isArray(items)) return false;
    const cartPerformaceUpdateMarker = this.#beforeCartUpdate(source);
    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body: this.#getCartBodyWithSections({ items }) }));
      return await this.#afterCartUpdate(response, source);
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      return false;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Copies `data-cart` from rendered section HTML onto live cart-items-components.
   * Hydration-mode morphs only update keyed subtrees and can leave host attributes stale.
   *
   * @param {Record<string, string | undefined>} sections
   */
  #syncDataCartFromSections(sections) {
    for (const [sectionId, sectionHtml] of Object.entries(sections)) {
      if (!sectionHtml) continue;
      this.#syncDataCartFromSectionHtml(sectionId, sectionHtml);
    }
  }

  /**
   * @param {string} sectionId
   * @param {string} sectionHtml
   */
  #syncDataCartFromSectionHtml(sectionId, sectionHtml) {
    const fragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const parsedComponents = fragment.querySelectorAll(`cart-items-component[data-section-id="${sectionId}"]`);

    for (const parsed of parsedComponents) {
      if (!(parsed instanceof HTMLElement) || !parsed.dataset.cart) continue;

      const selector = parsed.hasAttribute('data-drawer')
        ? `cart-items-component[data-section-id="${sectionId}"][data-drawer]`
        : `cart-items-component[data-section-id="${sectionId}"]:not([data-drawer])`;

      for (const live of document.querySelectorAll(selector)) {
        if (live instanceof HTMLElement) {
          live.dataset.cart = parsed.dataset.cart;
        }
      }
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#syncDataCartFromSections(parsedResponseText.sections);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);

    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, this.isDrawer ? 'hydration' : 'full');
      this.#syncDataCartFromSectionHtml(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
